import type { TFunction } from 'i18next';
import type {
  AccountabilitySiteDto,
  BusinessImpact,
  OrganizationManagerDashboardStatsDto,
  RepairQuarterCode,
} from '~/app/services/organization-manager-dashboard/organization-manager-dashboard.types';
import type {
  AccountabilitySiteViewModel,
  AccountabilityViewModel,
  PortfolioRowViewModel,
  RepairHoursViewModel,
} from './OrganizationManagerDashboardPage.types';

/** Initial mount only — not filter transitions with placeholder (previous) data. */
export function isInitialDataLoad(isPending: boolean, data: unknown): boolean {
  return isPending && data === undefined;
}

export function resolveGreetingPeriod(
  hour: number,
): 'morning' | 'afternoon' | 'evening' {
  if (hour < 12) return 'morning';
  if (hour < 18) return 'afternoon';
  return 'evening';
}

export function firstNameFromDisplayName(name: string | undefined): string {
  if (!name?.trim()) return '';
  return name.trim().split(/\s+/)[0] ?? '';
}

export function formatPenaltyEuros(amount: number, locale: string): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(amount);
}

export function formatRepairHours(hours: number): string {
  const rounded = Math.round(hours * 100) / 100;
  return `${rounded}h`;
}

export function formatHoursAmount(hours: number): string {
  return String(Math.round(hours * 100) / 100);
}

export function formatChargersRating(rating: number, max: number): string {
  return `${rating} / ${max}`;
}

export function businessImpactPillClass(impact: BusinessImpact): string {
  switch (impact) {
    case 'critical':
      return 'om-impact-pill om-impact-pill--critical';
    case 'warning':
      return 'om-impact-pill om-impact-pill--warning';
    case 'good':
      return 'om-impact-pill om-impact-pill--good';
  }
}

export function barHeightPercent(hours: number, maxHours: number): number {
  if (maxHours <= 0) return 0;
  return Math.min(100, Math.round((hours / maxHours) * 100));
}

export function targetLinePercent(
  targetHours: number,
  maxHours: number,
): number {
  if (maxHours <= 0) return 0;
  return Math.min(100, Math.round((targetHours / maxHours) * 100));
}

function formatSiteDetail(
  site: AccountabilitySiteDto,
  locale: string,
  t: TFunction<'dashboard'>,
): string {
  if (site.hoursLate != null && site.penaltyEuros != null) {
    return t('accountability.siteLateDetail', {
      hours: site.hoursLate,
      amount: formatPenaltyEuros(site.penaltyEuros, locale),
    });
  }
  return t('accountability.withinContractTerms');
}

function mapAccountabilitySite(
  site: AccountabilitySiteDto,
  locale: string,
  t: TFunction<'dashboard'>,
): AccountabilitySiteViewModel {
  return {
    id: site.id,
    siteName: site.siteName,
    status: site.status,
    statusLabel: t(`accountability.status.${site.status}`),
    detail: formatSiteDetail(site, locale, t),
  };
}

export function mapAccountability(
  stats: OrganizationManagerDashboardStatsDto,
  locale: string,
  t: TFunction<'dashboard'>,
): AccountabilityViewModel {
  const { accountability } = stats;
  return {
    activeBreaches: String(accountability.activeBreaches),
    activeBreachesLabel: t('accountability.activeBreaches'),
    penaltiesCaption: t('accountability.penaltiesCaption', {
      amount: formatPenaltyEuros(accountability.penaltiesTotalEuros, locale),
    }),
    slaLabel: t('accountability.slaCompliance'),
    slaRate: `${accountability.slaComplianceRatePercent}%`,
    slaCaption: t('accountability.slaCaption', {
      compliant: accountability.compliantSites,
      total: accountability.totalSites,
    }),
    sites: accountability.sites.map((site) =>
      mapAccountabilitySite(site, locale, t),
    ),
  };
}

const QUARTER_I18N_KEY: Record<RepairQuarterCode, string> = {
  Q1: 'repair.quarters.q1',
  Q2: 'repair.quarters.q2',
  Q3: 'repair.quarters.q3',
  Q4: 'repair.quarters.q4',
};

export function mapRepairHours(
  stats: OrganizationManagerDashboardStatsDto,
  t: TFunction<'dashboard'>,
): RepairHoursViewModel {
  const { repairHours } = stats;
  const maxHours = Math.max(
    ...repairHours.quarters.map((quarter) => quarter.hours),
    repairHours.targetHours,
  );

  return {
    title: t('repair.title'),
    subtitle: t('repair.subtitle', { target: repairHours.targetHours }),
    targetHours: repairHours.targetHours,
    targetLineLabel: t('repair.targetLine', {
      target: repairHours.targetHours,
    }),
    maxHours,
    insight: t('repair.insight', {
      peak: repairHours.peakHours,
      current: repairHours.currentHours,
      target: repairHours.targetHours,
    }),
    bars: repairHours.quarters.map((quarter) => ({
      id: quarter.quarter,
      quarter: quarter.quarter,
      label: t(QUARTER_I18N_KEY[quarter.quarter]),
      hours: quarter.hours,
      hoursLabel: formatRepairHours(quarter.hours),
      highlighted: quarter.highlighted,
    })),
  };
}

export function mapPortfolioRows(
  stats: OrganizationManagerDashboardStatsDto,
): PortfolioRowViewModel[] {
  return stats.portfolio.map((row) => ({
    id: row.id,
    location: row.location,
    chargersRating: formatChargersRating(
      row.chargersRating,
      row.chargersRatingMax,
    ),
    ownershipIssue: row.ownershipIssue,
    contractStatus: row.contractStatus,
    businessImpact: row.businessImpact,
  }));
}
